import { useQuery } from "@tanstack/react-query";
import { fetcher } from "@/shared/api";
import { categoryKeys } from "./queryKeys";
import type { CategoryTreeNode } from "../model/types";

function toQueryString(params: Record<string, unknown>): string {
  return new URLSearchParams(
    Object.fromEntries(
      Object.entries(params).filter(
        ([, v]) => v !== undefined && v !== null && v !== "",
      ),
    ) as Record<string, string>,
  ).toString();
}

interface CategoryListResponse {
  data:
    | {
        docs?: { id: string; [key: string]: unknown }[];
        [key: string]: unknown;
      }
    | { id: string; [key: string]: unknown }[];
}

export function useCategoryList(queryParams: Record<string, unknown> = {}) {
  const query = toQueryString(queryParams);
  return useQuery({
    queryKey: categoryKeys.list(queryParams),
    queryFn: () =>
      fetcher<CategoryListResponse>("/category-variant/categories?" + query),
  });
}

export function useCategoryListById(
  id: string,
  queryParams: Record<string, unknown> = {},
) {
  const query = toQueryString(queryParams);
  return useQuery({
    // With no query params the URL is byte-for-byte `useCategoryDetails`, so
    // it shares that hook's key. Keyed as a list it was a second cache entry
    // for the same resource, and the category detail page fetched it twice —
    // once for the page, once for the breadcrumb, which renders on every
    // route and looks the category up by id.
    queryKey: query
      ? [...categoryKeys.lists(), id, queryParams]
      : categoryKeys.detail(id),
    queryFn: () =>
      fetcher(
        "/category-variant/categories/" + id + (query ? "?" + query : ""),
      ),
    enabled: !!id,
  });
}

export function useCategoryDetails(id: string) {
  return useQuery({
    queryKey: categoryKeys.detail(id),
    queryFn: () => fetcher("/category-variant/categories/" + id),
    enabled: !!id,
  });
}

/**
 * Fetch the entire category tree in one request — every root with its nested
 * children, images, slugs and depth. Backed by GET
 * /category-variant/categories/tree (returns a nested array). One call powers
 * browsing, subcategory flyouts and client-side search; cached 5 minutes.
 */
export function useCategoryTree() {
  return useQuery<CategoryTreeNode[]>({
    queryKey: categoryKeys.tree(),
    queryFn: async () => {
      const res = await fetcher<unknown>("/category-variant/categories/tree");
      // The endpoint returns a nested array; some endpoints on this API answer
      // inside a { success, message, data } envelope, so unwrap defensively.
      if (Array.isArray(res)) return res as CategoryTreeNode[];
      const rows = (res as { data?: unknown })?.data;
      return Array.isArray(rows) ? (rows as CategoryTreeNode[]) : [];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Superadmin categories this store can still pull in, as a nested tree.
 * Needs `product.read`.
 *
 * `enabled` exists because the only consumer is a modal: fetching on page load
 * would spend a request on a dialog most visits never open, and a stale list
 * is worse than no list — it could offer a category that was synced since.
 * Kept unstale (`staleTime: 0`) for the same reason.
 */
export function useSyncAvailableCategories(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: categoryKeys.syncAvailable(),
    queryFn: () => fetcher("/category-variant/categories/sync/available"),
    enabled: options?.enabled ?? true,
    staleTime: 0,
  });
}

export function useCategoryEffectivePreset(categoryId: string) {
  return useQuery({
    queryKey: categoryKeys.effectivePreset(categoryId),
    queryFn: () =>
      fetcher(
        "/category-variant/categories/" + categoryId + "/effective-preset",
      ),
    enabled: !!categoryId,
  });
}

// `useCategoryVariantGroups` (GET /categories/:id/groups) was removed: that
// route does not exist on the backend, so every product-edit render fired a
// 404 and the hook always resolved to an empty list. The concept it served was
// superseded by the preset flow — `useCategoryEffectivePreset` resolves the
// category's preset id, and `usePresetDetail` (/category-variant/presets/:id)
// returns its option types and values. `VariantDrawer` already prefers that
// resolved preset, treating the old groups payload as a fallback only.

